<template>
  <modal-dialog
    :visible="dialog"
    persistent
    scrollable
    max-width="600px"
    :title="$t('title.update_password')"
  >
    <!--  -->
    <confirmation-dialog
      :visible="resetConfirmation"
      :message="$t('message.confirm_change_password')"
      @confirm="resetPassword"
      @close="resetConfirmation = false"
    />

    <!--  -->
    <v-form ref="form">
      <validation-observer ref="observer">
        <v-row>
          <v-col
            cols="12"
            class="pb-0 mb-0"
          >
            <validation-provider
              v-slot="{ errors }"
              rules="required|min:6|max:50"
            >
              <v-text-field
                v-model="password"
                class="pb-0 mb-0"
                type="password"
                :error-messages="errors"
                :label="$t('label.current_password')"
              />
            </validation-provider>
          </v-col>
        </v-row>
        <v-row>
          <v-col class="pt-0 mt-0">
            <div class="pt-0 text-center">
              <v-btn
                text
                color="primary"
                @click="resetConfirmation=true"
              >
                {{ $t('button.forgot_password') }}
              </v-btn>
            </div>
          </v-col>
        </v-row>

        <!--  -->
        <validation-observer>
          <v-row>
            <v-col
              class="pt-5"
              cols="12"
            >
              <password-text-field
                v-model="newPassword"
                :label="$t('label.new_password')"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col
              class="pt-0"
              cols="12"
            >
              <validation-provider
                v-slot="{ errors }"
                name="confirm"
                rules="required|max:50|password:@newPassword"
              >
                <v-text-field
                  v-model="confirmPassword"
                  class="pt-2"
                  type="password"
                  :error-messages="errors"
                  :label="$t('label.retype_password')"
                />
              </validation-provider>
            </v-col>
          </v-row>
        </validation-observer>
      </validation-observer>
    </v-form>

    <!--  -->
    <template #actions>
      <v-spacer />
      <v-btn
        color="success"
        @click="save"
      >
        {{ $t('button.save') }}
      </v-btn>
      <v-btn
        color="error"
        text
        @click="cancel"
      >
        {{ $t('button.cancel') }}
      </v-btn>
    </template>
  </modal-dialog>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue';
import ConfirmationDialog from '@/components/dialogs/ConfirmationDialog.vue';
import PasswordTextField from '@/components/forms/PasswordTextField.vue';
import { performAction } from '@/services/vue-utils';
import DialogClosable from '@/mixins/dialog-closable';
import { ValidationObserver } from 'vee-validate';
import toast from '@/plugins/toast-plugin/toast';

export default Vue.extend({
  name: 'ChangePasswordDialog',
  components: { ConfirmationDialog, PasswordTextField },
  mixins: [DialogClosable],
  props: {
    username: { type: String as PropType<string>, required: true },
  },
  data() {
    return {
      password: '',
      newPassword: '',
      confirmPassword: '',
      resetConfirmation: false,
    };
  },
  computed: { },
  watch: { },
  methods: {
    reset() {
      (this.$refs.form as Vue & { reset: () => void }).reset();
      (this.$refs.observer as InstanceType<typeof ValidationObserver>).reset();
    },
    async save() {
      const isValid = await (this.$refs.observer as InstanceType<typeof ValidationObserver>).validate();
      if (!isValid) {
        toast.error(this.$i18n.tc('error.validation_failed'));
        return;
      }

      const ok = await performAction(
        this.$i18n.tc('message.your_password_has_been_updated'),
        this.$i18n.tc('error.changing_password'),
        async () => this.$tstore.dispatch('changePassword', {
          username: this.username,
          password: this.password,
          newPassword: this.newPassword,
        }),
      );
      if (ok) {
        this.reset();
        this.$emit('close');
      }
    },
    cancel() {
      this.reset();
      this.$emit('close');
    },
    async resetPassword() {
      const ok = await performAction(
        this.$i18n.tc('message.success_new_password_sent'),
        this.$i18n.tc('error.changing_password'),
        async () => this.$tstore.dispatch('resetPassword', {
          username: this.username,
          password: this.password,
          newPassword: this.newPassword,
        }),
      );

      if (ok) {
        this.resetConfirmation = false;
      }
    },
  },
});
</script>
